import React, { useEffect, useRef, useState } from "react";
import { searchSocieties } from "./api";

const styles = {
    shell: {
        // pill container
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        // maxWidth: 720,
        height: 55,
        padding: "0 ",
        borderRadius: 9999,
        background: "#FFFFFF",
        border: "none",
        boxShadow: "none",
    },
    input: {
        flex: 1,
        height: "100%",
        border: "none",
        outline: "none",
        fontSize: 14,
        color: "#1F2937",
        background: "transparent",
    },
    icon: { width: 18, height: 18, flex: "0 0 18px", color: "#98A2B3", opacity: 0.9 },
    spinnerDot: {
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "2px solid #e5e7eb",
        borderTopColor: "#94a3b8",
        animation: "sa-spin 0.8s linear infinite",
    },
    dropdown: {
        position: "absolute",
        top: 56,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: "#FFFFFF",
        border: "1px solid #E6E7EB",
        borderRadius: 12,
        boxShadow: "0 16px 36px rgba(0,0,0,0.12)",
        overflow: "hidden",
        maxHeight: 320,
        overflowY: "auto",
    },
    row: (active) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px",
        cursor: "pointer",
        background: active ? "#F6F7FB" : "#FFFFFF",
        transition: "background-color 120ms ease",
    }),
    rowText: { color: "#111827", fontSize: 14, fontWeight: 500 },
    pdfIcon: { marginLeft: "auto", color: "#DC2626" },
};

/* keyframes for spinner */
const SpinnerStyle = () => (
    <style>
        {`@keyframes sa-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}
    </style>
);

const SearchAutocomplete = ({
                                onSelect,
                                placeholder = "Search ...",
                                className = "",
                                filterAuthority
                            }) => {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [debounceTimer, setDebounceTimer] = useState(null);

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // debounce search
    useEffect(() => {
        if (debounceTimer) clearTimeout(debounceTimer);

        if (!query.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const t = setTimeout(async () => {
            try {
                const results = await searchSocieties(query,filterAuthority);
                setSuggestions(results || []);
                setShowSuggestions(true);
                setSelectedIndex(-1);
            } catch (e) {
                console.error("Autocomplete search error:", e);
                setSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        }, 300);

        setDebounceTimer(t);
        return () => clearTimeout(t);
    }, [query]);

    const handleSuggestionClick = (society) => {
        setQuery(society.name || "");
        setShowSuggestions(false);
        onSelect?.(society);
        if (society.pdf_url) {
            window.open(society.pdf_url, "_blank", "noopener,noreferrer");
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((i) => (i < suggestions.length - 1 ? i + 1 : i));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((i) => (i > 0 ? i - 1 : -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (selectedIndex >= 0) handleSuggestionClick(suggestions[selectedIndex]);
            else handleSuggestionClick(suggestions[0]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) setShowSuggestions(true);
    };

    return (
        <div ref={wrapperRef} className={className} style={{ position: "relative" ,width:'100%'}}>
            <SpinnerStyle />
            {/* Pill search box */}
            <div style={styles.shell}>

                <img src="search-icon.svg" width="20px" height="20px" alt="" />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    autoComplete="off"
                    style={styles.input}
                />

                {isLoading && <span style={styles.spinnerDot} />}
            </div>

            {/* Suggestions */}
            {showSuggestions && (
                <div style={styles.dropdown}>
                    {suggestions.length > 0 ? (
                        suggestions.map((s, idx) => (
                            <div
                                key={s.id}
                                style={styles.row(idx === selectedIndex)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                                onMouseLeave={() => setSelectedIndex(-1)}
                                onClick={() => handleSuggestionClick(s)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && handleSuggestionClick(s)}
                            >
                                {/* building glyph */}
                                <svg
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    style={{ color: "#6B7280" }}
                                >
                                    <path
                                        fill="currentColor"
                                        d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zM7 9h2V7H7v2zm0 4h2v-2H7v2zm0 4h2v-2H7v2zM11 9h2V7h-2v2zm0 4h2v-2h-2v2zm0 4h2v-2h-2v2zM15 9h2V7h-2v2zm0 4h2v-2h-2v2zm0 4h2v-2h-2v2zM19 3H5c-1.1 0-2 .9-2 2v2h18V5c0-1.1-.9-2-2-2zm2 6H3c-1.1 0-2 .9-2 2v10h6v-4h10v4h6V11c0-1.1-.9-2-2-2z"
                                    />
                                </svg>
                                <span style={styles.rowText}>{s.name}</span>
                                {s.pdf_url && (
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="18"
                                        height="18"
                                        style={styles.pdfIcon}
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm1 7V3.5L18.5 9H15z"
                                        />
                                    </svg>
                                )}
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: 14, color: "#6B7280", textAlign: "center" }}>
                            No results found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
